/**
 * A candidate's offer-tracking events (and recording a new one), fetched and
 * written outside the jobs state machine.
 *
 * Same root cause as useCandidateInterview, and the same fix shape: the
 * candidate page fires three fetches back to back in one commit (candidate,
 * interview, offer events). jobsFSM only accepts an event while it is idle,
 * and the first one (GET_CANDIDATE) moves it into a busy state with no
 * handlers - so FETCH_OFFER_EVENTS, dispatched moments later in the same
 * commit, was silently dropped by XState every time, for every candidate.
 * offerEvents stayed empty regardless of what had actually been recorded.
 *
 * Recording is moved out too, not left split across two sources of truth:
 * RECORD_OFFER_EVENT's own FSM state appended the new event directly into
 * `context.offerEvents`, which only worked because that array also lived in
 * the FSM. Once the read moves to this hook's own state, the FSM's copy is
 * permanently empty and appending to it would no longer reflect what the
 * recruiter can see. Owning both here keeps one array, one source of truth -
 * and incidentally also stops a recording failure from sending the whole
 * jobs app to its global, exitless ERROR state, which is what RECORD_OFFER_
 * EVENT's onError did.
 */

import { useCallback, useRef, useState } from 'react';
import axios from '../../../axiosConfig';
import type { CandidateOfferEvent, CandidateOfferEventType } from '../../../fsm/jobs';
import type { RecordOfferEventPayload } from '../components/candidate/record-offer-event-modal';

export type CandidateOfferEventsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export const useCandidateOfferEvents = () => {
  const [events, setEvents] = useState<CandidateOfferEvent[]>([]);
  const [status, setStatus] = useState<CandidateOfferEventsStatus>('idle');
  const [isRecording, setIsRecording] = useState(false);
  // Same ticketing as useCandidateInterview: a reply is only applied if it is
  // still the latest request - a recruiter who has moved to another
  // candidate must not have a slow reply for the old one land on top.
  const latestRequest = useRef(0);

  const load = useCallback(async (jobId: number, candidateId: number) => {
    const request = ++latestRequest.current;
    // Drop the previous candidate's events at once: they must not show
    // under this candidate while its own are loading.
    setEvents([]);
    setStatus('loading');
    try {
      const response = await axios.get<CandidateOfferEvent[]>(
        `/api/jobs/${jobId}/candidates/${candidateId}/offer-events`,
      );
      if (request !== latestRequest.current) return;
      setEvents(response.data);
      setStatus('loaded');
    } catch {
      if (request !== latestRequest.current) return;
      setEvents([]);
      setStatus('error');
    }
  }, []);

  const record = useCallback(
    async (
      jobId: number,
      candidateId: number,
      eventType: CandidateOfferEventType,
      payload: RecordOfferEventPayload,
    ) => {
      setIsRecording(true);
      try {
        const response = await axios.post<CandidateOfferEvent>(
          `/api/jobs/${jobId}/candidates/${candidateId}/offer-events`,
          { eventType, ...payload },
        );
        setEvents((current) => [...current, response.data]);
      } finally {
        setIsRecording(false);
      }
    },
    [],
  );

  return { events, status, isRecording, load, record };
};
